// ============================================================================
// WorkPay Investor Pitch Deck — content (single editable source of truth)
// Content is APPROVED. Edit copy here; layouts read from these objects.
// ============================================================================

import { ASSETS } from "@/data/content";

// Deck-specific brand palette (neon lime accent, light/editorial)
export const DECK = {
  accent: "#C1FF72", // WorkPay neon green
  accentDeep: "#A6E94B", // slightly deeper for small text/borders on white
  ink: "#0B0B0F", // near-black headlines
  charcoal: "#26262B", // body dark
  muted: "#6B7280", // muted grey text
  bg: "#FFFFFF",
  offwhite: "#FAFAF7",
  grey: "#F3F3F0",
  paleGreen: "#F1FBDE", // pale green tint for cards/blocks
};

export const PITCH_META = {
  brand: "WorkPay",
  tagline: "The payment trust layer for service businesses.",
  raisingAmount: "USD 350K",
  contact: "support@workpay-app.com",
};

// ---------------------------------------------------------------------------
// CORE DECK — 12 slides
// ---------------------------------------------------------------------------
export const CORE_SLIDES = [
  {
    id: "cover",
    type: "cover",
    section: "core",
    title: "WorkPay",
    subheadline: "Giving power back to service vendors while keeping customers safe.",
    support: "The payment trust layer for service businesses.",
    flow: ["Vendor", "Protected Payment", "Customer Approval", "Vendor Payout"],
    image: ASSETS.deckCover,
    imageAlt: "WorkPay vendor and customer",
  },
  {
    id: "problem",
    type: "problem",
    section: "core",
    kicker: "The Problem",
    headline: "Work should not end with begging.",
    subheadline: "Vendors do the work. Then come the excuses.",
    quotes: [
      "We’ll pay next week.",
      "Boss is not available.",
      "Send the invoice again.",
      "Just one small extra change.",
      "We’ll release after final delivery.",
      "Can you reduce the price now?",
    ],
    bottomHeadline: "WorkPay gives vendors a better default.",
    support: "Service vendors are tired of chasing money they already earned.",
  },
  {
    id: "case-study",
    type: "caseStudy",
    section: "core",
    kicker: "Case Study",
    headline: "We built WorkPay because we lived this.",
    number: "USD 30,000+",
    subheadline: "Unpaid invoices lost by 36Hour Company in 2 years.",
    costs: ["Lost revenue", "Lost cash flow", "Lost profit", "Lost momentum"],
    small: "When service businesses don’t get paid, they still carry the cost.",
    image: ASSETS.vendor,
    imageAlt: "Service business owner",
  },
  {
    id: "market-pain",
    type: "marketPain",
    section: "core",
    kicker: "Market Pain",
    headline: "This is not rare. It is normal.",
    subheadline: "Unpaid and delayed payments are a service-business problem everywhere.",
    stats: [
      { value: "56%", label: "of small businesses surveyed were owed money from unpaid invoices", source: "QuickBooks 2025 small-business report" },
      { value: "$17.5K", label: "average unpaid amount per business", source: "QuickBooks 2025 small-business report" },
      { value: "55–58%", label: "of global freelancers say they completed work and were never paid", source: "Global freelance industry reports" },
      { value: "51%", label: "of UAE B2B credit sales are affected by late payments", source: "UAE B2B payment data" },
      { value: "4%", label: "of UAE B2B invoices end up as bad debt", source: "UAE B2B payment data" },
    ],
    bottom: ["This is not a payment issue.", "It is a trust issue."],
  },
  {
    id: "core-truth",
    type: "coreTruth",
    section: "core",
    kicker: "Core Truth",
    headline: ["Clients don’t trust vendors.", "Vendors fear non-payment."],
    subheadline: "Both sides are exposed. The vendor usually carries more risk.",
    customer: {
      title: "Customer fear",
      image: ASSETS.customer,
      points: [
        "Will I get what I paid for?",
        "What if the vendor disappears?",
        "What if the work is poor?",
      ],
    },
    vendor: {
      title: "Vendor fear",
      image: ASSETS.vendor,
      points: [
        "What if the client delays payment?",
        "What if they ask for more after delivery?",
        "What if they give irrelevant excuses?",
        "What if they simply don’t pay?",
      ],
    },
    bottom: "Today, the vendor often works at the mercy of the customer.",
  },
  {
    id: "solution",
    type: "solution",
    section: "core",
    kicker: "The Solution",
    headline: "Put a trusted party in the middle.",
    subheadline: "That trusted party is WorkPay.",
    support: [
      "Customers need protection before they pay.",
      "Vendors need certainty before they start.",
      "WorkPay gives both sides a safer way to transact.",
    ],
    points: [
      "Customer pays into a protected project",
      "Vendor starts with confidence",
      "Work gets submitted by milestone",
      "Customer approves",
      "Vendor gets paid",
      "If something goes wrong, there is a structured resolution flow",
    ],
    bottom: ["Customers stay safe.", "Vendors stop begging for payment."],
    flow: ["Customer", "WorkPay", "Vendor"],
  },
  {
    id: "how-it-works",
    type: "howItWorks",
    section: "core",
    kicker: "How It Works",
    headline: ["Simple for customers.", "Safer for vendors."],
    steps: [
      "Vendor creates project or payment link",
      "Customer pays",
      "Funds are protected",
      "Vendor delivers milestone",
      "Customer approves",
      "Vendor gets paid",
    ],
  },
  {
    id: "product",
    type: "product",
    section: "core",
    kicker: "Product",
    headline: "One link. Clear milestones. Protected payouts.",
    screens: [
      { variant: "create", label: "Vendor creates project" },
      { variant: "payment", label: "Customer checkout" },
      { variant: "approval", label: "Milestone approval" },
      { variant: "payout", label: "Vendor payout dashboard" },
      { variant: "dispute", label: "Dispute / refund view" },
    ],
  },
  {
    id: "business-model",
    type: "businessModel",
    section: "core",
    kicker: "Business Model",
    headline: "Revenue built into the flow.",
    cards: [
      "Customer payment protection fee",
      "Vendor payment processing fee",
      "Premium vendor tools later",
      "Corporate / API workflow later",
    ],
    levers: [
      "More vendors",
      "Higher project value",
      "More repeat customers",
      "More milestones per project",
      "Referral loop",
    ],
  },
  {
    id: "growth-loop",
    type: "growthLoop",
    section: "core",
    kicker: "Growth Loop",
    headline: "Trust creates repeat usage.",
    loop: [
      "Vendor uses WorkPay",
      "Customer feels safe",
      "Deal closes faster",
      "Vendor uses it again",
      "Customer recommends it",
      "More vendors join",
    ],
    support: "The better the trust, the faster the adoption.",
  },
  {
    id: "traction",
    type: "traction",
    section: "core",
    kicker: "Traction · Proof of Concept",
    headline: "Real money is already moving through WorkPay.",
    number: "AED 43,000+",
    numberLabel: "processed through WorkPay during private testing.",
    stats: [
      { value: "20+", label: "live payments completed" },
      { value: "AED 16,000", label: "single upfront payment on one signage project" },
    ],
    story: {
      quote: "The client didn’t trust the vendor. They trusted WorkPay in the middle.",
      body: "On a signage project, a customer paid AED 16,000 upfront — something they would never have done on trust alone. With WorkPay sitting in the middle, the vendor started with certainty and the customer felt protected. Both sides walked away happy.",
    },
    bottom: ["Vendor: paid with confidence.", "Customer: protected before paying."],
    note: "Live transactions during private product testing, before public launch.",
  },
  {
    id: "team",
    type: "team",
    section: "core",
    kicker: "Team",
    headline: "Built by operators who know this pain.",
    founders: [
      { name: "Roshan Hegde", role: "CEO / CMO", initials: "RH", bio: "Built and scaled 36Hour Company. Experienced unpaid invoices and service-business payment risk firsthand." },
      { name: "Umar", role: "CTO", initials: "U", bio: "Engineer with experience in large-scale banking / government systems." },
      { name: "Shams", role: "COO", initials: "S", bio: "Focused on operations, execution, and customer / vendor support." },
    ],
  },
  {
    id: "vision",
    type: "vision",
    section: "core",
    kicker: "Vision / Ask",
    headline: "We are building the trust layer for service businesses.",
    subheadline: "The future of service payments is protected, milestone-based, and vendor-first.",
    raising: PITCH_META.raisingAmount,
    useOfFunds: [
      "Product development",
      "Compliance and legal",
      "Vendor acquisition",
      "Marketing and content",
      "Partnerships",
      "Risk and dispute operations",
    ],
    closing: "Let’s rebuild how service businesses get paid.",
  },
];

// ---------------------------------------------------------------------------
// APPENDIX — optional supporting slides
// ---------------------------------------------------------------------------
export const APPENDIX_SLIDES = [
  {
    id: "appendix-divider",
    type: "divider",
    section: "appendix",
    kicker: "Appendix",
    headline: "Supporting detail.",
    subheadline: "Deeper context for the questions investors ask next.",
  },
  {
    id: "market-size",
    type: "appendixStats",
    section: "appendix",
    kicker: "Appendix · Market Size",
    headline: "A large, underserved service economy.",
    stats: [
      { value: "Service-first", label: "Vendors, freelancers, agencies and project businesses across MENA and beyond" },
      { value: "B2B + B2C", label: "Every project where work happens before payment is settled" },
      { value: "UAE first", label: "Launch market with high B2B late-payment exposure, then expand" },
    ],
    note: "Bottom-up: vendors × projects × average project value × take rate.",
  },
  {
    id: "competition",
    type: "appendixTable",
    section: "appendix",
    kicker: "Appendix · Competition",
    headline: "Payment links collect money. WorkPay builds trust.",
    columns: ["Bank transfer", "Payment links", "WorkPay"],
    rows: [
      { label: "Collects payment", a: true, b: true, c: true },
      { label: "Milestone tracking", a: false, b: false, c: true },
      { label: "Customer approval flow", a: false, b: false, c: true },
      { label: "Protected payout", a: false, b: false, c: true },
      { label: "Dispute documentation", a: false, b: false, c: true },
      { label: "Vendor-first design", a: false, b: false, c: true },
    ],
  },
  {
    id: "admin-risk",
    type: "appendixPanel",
    section: "appendix",
    kicker: "Appendix · Admin & Risk",
    headline: "Risk and dispute operations, built in.",
    points: [
      "Identity and business verification before payout",
      "Structured milestone evidence and proof trail",
      "Chargeback and refund handling workflow",
      "Fraud and risk monitoring on every project",
      "Compliance-ready records and audit logs",
    ],
    panelVariant: "dispute",
  },
  {
    id: "roadmap",
    type: "appendixRoadmap",
    section: "appendix",
    kicker: "Appendix · Roadmap",
    headline: "From early access to the default payment layer.",
    phases: [
      { tag: "Now", title: "Early access", items: ["UAE vendors", "Project links", "Milestones & approvals", "Daily payouts"] },
      { tag: "Next", title: "Scale", items: ["Vendor dashboard", "Premium tools", "Referral loop", "More categories"] },
      { tag: "Later", title: "Platform", items: ["Corporate / API", "Regional expansion", "Risk automation", "Partnerships"] },
    ],
  },
  {
    id: "projections",
    type: "appendixStats",
    section: "appendix",
    kicker: "Appendix · Projections",
    headline: "Revenue compounds with every milestone.",
    stats: [
      { value: "Take rate", label: "Protection + processing fee on each protected project" },
      { value: "Repeat", label: "Repeat vendors and customers lift lifetime value" },
      { value: "Expansion", label: "Premium tools and corporate / API add revenue layers" },
    ],
    note: "Detailed financial model available on request.",
  },
  {
    id: "corporate-api",
    type: "appendixPanel",
    section: "appendix",
    kicker: "Appendix · Corporate / API",
    headline: "A trust layer other platforms can plug into.",
    points: [
      "Embed protected project payments via API",
      "Milestone and approval flows for marketplaces",
      "Corporate vendor onboarding and controls",
      "White-label payout and dispute tooling",
    ],
    panelVariant: "payout",
  },
];

export const ALL_SLIDES = [...CORE_SLIDES, ...APPENDIX_SLIDES];
